import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from '../core/services/auth.service';

interface NavItem {
  key: string;
  path?: string;
  label: string;
  icon: string;
  comingSoon?: boolean;
  sectionLabel?: string;
  children?: NavChildItem[];
}

interface NavChildItem {
  key: string;
  path?: string;
  label: string;
  comingSoon?: boolean;
  children?: NavChildItem[];
}

@Component({
  selector: 'app-officer-shell',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet, RouterLink, RouterLinkActive],
  templateUrl: './officer-shell.component.html',
  styleUrl: './officer-shell.component.scss'
})
export class OfficerShellComponent {
  readonly brandLogoUrl = '/jci-nusantara-logo.png';
  logoLoadFailed = false;

  readonly navItems: NavItem[] = [
    { key: 'home', path: '/dashboard', label: 'Dashboard', icon: 'home', sectionLabel: 'Main' },
    {
      key: 'events',
      label: 'Events',
      icon: 'calendar',
      sectionLabel: 'Operations',
      children: [
        { key: 'events-published', path: '/events/published', label: 'Published' },
        { key: 'events-draft', path: '/events/draft', label: 'Draft' },
        { key: 'events-categories', path: '/events/categories', label: 'Categories' }
      ]
    },
    {
      key: 'sales',
      label: 'Sales',
      icon: 'receipt',
      children: [
        { key: 'sales-orders', path: '/sales/orders', label: 'Orders', comingSoon: true },
        { key: 'sales-events', path: '/sales/events', label: 'Ticket Orders' },
        { key: 'sales-memberships', path: '/sales/memberships', label: 'Memberships' },
        {
          key: 'sales-payments',
          label: 'Payments',
          children: [
            { key: 'sales-payments-all', path: '/sales/payments/all', label: 'All payments' },
            { key: 'sales-payments-receipts', path: '/sales/payments/receipts', label: 'Receipts', comingSoon: true }
          ]
        },
        {
          key: 'sales-analytics',
          label: 'Analytics',
          children: [
            { key: 'sales-analytics-overview', path: '/sales/analytics/overview', label: 'Sales overview' },
            { key: 'sales-analytics-store', path: '/sales/analytics/store', label: 'Store analytics', comingSoon: true },
            { key: 'sales-analytics-events', path: '/sales/analytics/events', label: 'Events analytics' },
            { key: 'sales-analytics-memberships', path: '/sales/analytics/memberships', label: 'Memberships analytics' }
          ]
        }
      ]
    },
    {
      key: 'portfolio',
      label: 'Portfolio',
      icon: 'briefcase',
      children: [
        { key: 'portfolio-projects', path: '/projects/projects', label: 'Projects' },
        { key: 'portfolio-collections', path: '/projects/collections', label: 'Collections' }
      ]
    },
    {
      key: 'marketing',
      label: 'Marketing',
      icon: 'megaphone',
      comingSoon: true,
      children: [
        { key: 'marketing-seo-geo', path: '/marketing/seo-geo', label: 'SEO & GEO', comingSoon: true },
        { key: 'marketing-google-ads', path: '/marketing/google-ads', label: 'Google Ads', comingSoon: true },
        { key: 'marketing-facebook-instagram-ads', path: '/marketing/facebook-instagram-ads', label: 'Facebook & Instagram Ads', comingSoon: true },
        { key: 'marketing-email', path: '/marketing/email-marketing', label: 'Email Marketing', comingSoon: true },
        { key: 'marketing-social', path: '/marketing/social-media-marketing', label: 'Social Media Marketing', comingSoon: true },
        { key: 'marketing-referral', path: '/marketing/referral-program', label: 'Referral Program', comingSoon: true },
        { key: 'marketing-gbp', path: '/marketing/google-business-profile', label: 'Google Business Profile', comingSoon: true }
      ]
    },
    {
      key: 'people',
      label: 'People',
      icon: 'users',
      sectionLabel: 'People & Commerce',
      children: [
        { key: 'people-members', path: '/people/members', label: 'Members' },
        { key: 'people-guests', path: '/people/guests', label: 'Guests' }
      ]
    },
    {
      key: 'commerce',
      label: 'Commerce',
      icon: 'store',
      children: [
        { key: 'commerce-store-catalog', path: '/commerce/store-catalog', label: 'Store Catalog', comingSoon: true },
        { key: 'commerce-membership-plans', path: '/commerce/membership-plans', label: 'Membership Plans' }
      ]
    },
    {
      key: 'organisation',
      label: 'Organisation',
      icon: 'building',
      sectionLabel: 'Organisation',
      children: [
        { key: 'org-board',       path: '/organisation/board-of-directors', label: 'Board of Directors' },
        { key: 'org-presidents',  path: '/organisation/presidents',         label: 'Presidents' }
      ]
    },
    { key: 'analytics', path: '/analytics', label: 'Analytics', icon: 'chart', comingSoon: true, sectionLabel: 'Tools' },
    { key: 'reports', path: '/reports', label: 'Reports', icon: 'report' },
    { key: 'flows', path: '/flows', label: 'Flows / Automation', icon: 'spark' },
    {
      key: 'content',
      label: 'Website Content',
      icon: 'file',
      children: [
        { key: 'content-home',          path: '/website-content/home',          label: 'Home Page' },
        { key: 'content-about',         path: '/website-content/about',         label: 'About Page' },
        { key: 'content-settings',      path: '/website-content/settings',      label: 'Site Settings' },
        { key: 'content-announcements', path: '/website-content/announcements', label: 'Announcements' }
      ]
    }
  ];
  sidebarCollapsed = false;
  mobileMenuOpen = false;
  sidebarFilter = '';
  private readonly openSections = new Set<string>();
  private readonly openChildSections = new Set<string>();

  constructor(
    readonly auth: AuthService,
    private readonly router: Router
  ) {}

  get displayName(): string {
    const user = this.auth.user();
    const firstName = typeof user?.['first_name'] === 'string' ? user['first_name'].trim() : '';
    const lastName = typeof user?.['last_name'] === 'string' ? user['last_name'].trim() : '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (fullName) return fullName;

    const email = typeof user?.['email'] === 'string' ? user['email'].trim() : '';
    if (email.includes('@')) {
      const local = email.split('@')[0].replace(/[._-]+/g, ' ').trim();
      if (local) {
        return local
          .split(' ')
          .filter(Boolean)
          .map((part) => part[0].toUpperCase() + part.slice(1))
          .join(' ');
      }
    }

    return 'Officer';
  }

  get currentPageTitle(): string {
    const url = this.router.url;
    for (const item of this.navItems) {
      if (item.path && url === item.path) return item.label;
      for (const child of item.children || []) {
        if (child.path && url.startsWith(child.path)) return child.label;
        for (const grandchild of child.children || []) {
          if (grandchild.path && url.startsWith(grandchild.path)) return grandchild.label;
        }
      }
    }
    return 'Dashboard';
  }

  get filteredNavItems(): NavItem[] {
    const query = this.sidebarFilter.trim().toLowerCase();
    if (!query) return this.navItems;

    return this.navItems
      .map((item) => {
        const itemMatches = item.label.toLowerCase().includes(query);
        const childMatches = (item.children || []).filter((child) =>
          child.label.toLowerCase().includes(query)
        );
        if (itemMatches) return item;
        if (childMatches.length) return { ...item, children: childMatches };
        return null;
      })
      .filter((item): item is NavItem => item !== null);
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
  }

  toggleSection(itemKey: string): void {
    if (this.openSections.has(itemKey)) {
      this.openSections.clear();
      this.openChildSections.clear();
    } else {
      this.openSections.clear();
      this.openSections.add(itemKey);
      this.openChildSections.clear();
      this.ensureSectionVisible(itemKey);
    }
  }

  isSectionOpen(itemKey: string): boolean {
    return this.openSections.has(itemKey);
  }

  isItemActive(item: NavItem): boolean {
    return this.isNodeActive(item.path, item.children);
  }

  isChildActive(child: NavChildItem): boolean {
    return this.isNodeActive(child.path, child.children);
  }

  toggleChildSection(itemKey: string): void {
    if (this.openChildSections.has(itemKey)) {
      this.openChildSections.clear();
    } else {
      this.openChildSections.clear();
      this.openChildSections.add(itemKey);
    }
  }

  isChildSectionOpen(itemKey: string): boolean {
    return this.openChildSections.has(itemKey);
  }

  iconPath(icon: string): string {
    const icons: Record<string, string> = {
      home: 'M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5',
      calendar: 'M7 2v4M17 2v4M3 9h18M4 6h16a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
      receipt: 'M6 3h12v18l-2.5-1.6L13 21l-2.5-1.6L8 21l-2-1.3V3Zm3 5h6m-6 4h6m-6 4h4',
      briefcase: 'M9 6V4h6v2m6 3H3v10h18V9ZM3 9l4 4h10l4-4',
      megaphone: 'M3 11v2l11 4V7L3 11Zm11 2h3a4 4 0 0 0 0-8h-3',
      users: 'M16 11a4 4 0 1 0-4-4 4 4 0 0 0 4 4ZM8 13a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm8 1c-3.2 0-6 1.7-6 4v2h12v-2c0-2.3-2.8-4-6-4ZM8 15c-2.8 0-5 1.4-5 3.4V20h5',
      store: 'M4 7h16l-1 4H5L4 7Zm1 4h14v10H5V11Zm3 3h3m2 0h3',
      chart: 'M5 19V9m7 10V5m7 14v-7M3 21h18',
      spark: 'M12 2l2.2 5.6L20 10l-5.8 2.4L12 18l-2.2-5.6L4 10l5.8-2.4L12 2Z',
      file: 'M7 3h7l5 5v13H7V3Zm7 1v5h5',
      building: 'M4 21V8l8-5 8 5v13M9 21V13h6v8M4 12h16',
      report: 'M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 0 2-2h2a2 2 0 0 0 2 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01'
    };

    return icons[icon] || icons['file'];
  }

  private isNodeActive(path?: string, children?: NavChildItem[]): boolean {
    if (path && this.router.url.startsWith(path)) return true;
    return (children || []).some((child) => this.isNodeActive(child.path, child.children));
  }

  logout(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }

  onLogoError(): void {
    this.logoLoadFailed = true;
  }

  private ensureSectionVisible(itemKey: string): void {
    requestAnimationFrame(() => {
      const nav = document.querySelector('.metronic-nav') as HTMLElement | null;
      const section = document.querySelector(`[data-nav-section="${itemKey}"]`) as HTMLElement | null;
      if (!nav || !section) return;

      const sectionTop = section.offsetTop;
      const sectionBottom = sectionTop + section.offsetHeight;
      const viewportTop = nav.scrollTop;
      const viewportBottom = viewportTop + nav.clientHeight;

      if (sectionBottom > viewportBottom) {
        nav.scrollTop = sectionBottom - nav.clientHeight + 10;
      } else if (sectionTop < viewportTop) {
        nav.scrollTop = Math.max(0, sectionTop - 10);
      }
    });
  }
}
